import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFDownloadLink,
  pdf,
} from "@react-pdf/renderer";
import type { IMatchSummary } from "../views/leaguesHome/tournaments/tournamentsDetails/matches/types/IMatches";
import type { IPlayers } from "../views/leaguesHome/teams/types/teams";
import type { ITeamsSquad } from "../views/leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import { formatPlayerRoleLabel } from "../helper/helper";

// Helper function to format date
const formatDate = (dateString?: string): string => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
};

// Styles for PDF
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
    fontFamily: "Helvetica",
  },
  title: {
    fontSize: 24,
    marginBottom: 10,
    fontWeight: "bold",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 20,
    textAlign: "center",
    color: "#666",
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 10,
    marginTop: 15,
    color: "#433fac",
  },
  table: {
    display: "flex",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#bfbfbf",
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#bfbfbf",
    borderBottomStyle: "solid",
  },
  tableHeader: {
    backgroundColor: "#433fac",
    color: "#fff",
    fontWeight: "bold",
  },
  tableCell: {
    padding: 8,
    fontSize: 10,
    borderRightWidth: 1,
    borderRightColor: "#bfbfbf",
    borderRightStyle: "solid",
  },
  tableCellLast: {
    padding: 8,
    fontSize: 10,
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    fontSize: 10,
    color: "#666",
  },
  matchCard: {
    marginBottom: 15,
    padding: 10,
    borderWidth: 1,
    borderColor: "#bfbfbf",
    borderRadius: 5,
  },
  matchTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 8,
  },
  matchRow: {
    flexDirection: "row",
    marginBottom: 5,
  },
  matchLabel: {
    fontWeight: "bold",
    width: 100,
  },
  matchValue: {
    flex: 1,
  },
});

// Helper functions
const getPlayerName = (m: IPlayers | ITeamsSquad): string => {
  if ("playerFirstName" in m && "playerLastName" in m) {
    return `${m.playerFirstName || ""} ${m.playerLastName || ""}`.trim();
  }
  return "N/A";
};

const getPlayerRoleRaw = (m: IPlayers | ITeamsSquad): string => {
  if ("playerRole" in m) {
    return (m.playerRole ?? "").trim();
  }
  return "";
};

const getPlayerRoleDisplay = (m: IPlayers | ITeamsSquad): string => {
  const raw = getPlayerRoleRaw(m);
  return raw ? formatPlayerRoleLabel(raw) : "N/A";
};

// Members PDF Component
const MembersPDF: React.FC<{
  members: IPlayers[] | ITeamsSquad[];
  teamName: string;
}> = ({ members, teamName }) => {
  const batters = members.filter((m) => {
    const role = getPlayerRoleRaw(m).toLowerCase();
    return role.includes("batter") || role.includes("bat");
  });
  const bowlers = members.filter((m) =>
    getPlayerRoleRaw(m).toLowerCase().includes("bowler")
  );
  const allRounders = members.filter((m) => {
    const role = getPlayerRoleRaw(m);
    return role.toLowerCase().includes("all") || role === "BAT_ALL_ROUNDER";
  });
  const wicketKeepers = members.filter((m) => {
    const role = getPlayerRoleRaw(m);
    return role.toLowerCase().includes("wicket") || role === "WICKET_KEEPER";
  });

  const renderTable = (
    title: string,
    data: (IPlayers | ITeamsSquad)[],
    columns: string[]
  ) => {
    if (data.length === 0) return null;

    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{title}</Text>
        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHeader]}>
            {columns.map((col, idx) => (
              <Text
                key={idx}
                style={[
                  styles.tableCell,
                  idx === columns.length - 1 && styles.tableCellLast,
                  { width: `${100 / columns.length}%` },
                ]}
              >
                {col}
              </Text>
            ))}
          </View>
          {data.map((member, index) => {
            const roleRaw = getPlayerRoleRaw(member);
            const roleKey = roleRaw.toUpperCase();
            return (
              <View key={index} style={styles.tableRow}>
                <Text
                  style={[
                    styles.tableCell,
                    { width: `${100 / columns.length}%` },
                  ]}
                >
                  {index + 1}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    { width: `${100 / columns.length}%` },
                  ]}
                >
                  {getPlayerName(member)}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    { width: `${100 / columns.length}%` },
                  ]}
                >
                  {getPlayerRoleDisplay(member)}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    { width: `${100 / columns.length}%` },
                  ]}
                >
                  {roleKey === "CAPTAIN" ? "Yes" : "No"}
                </Text>
                <Text
                  style={[
                    styles.tableCellLast,
                    { width: `${100 / columns.length}%` },
                  ]}
                >
                  {roleKey === "VICE_CAPTAIN" ? "Yes" : "No"}
                </Text>
              </View>
            );
          })}
        </View>
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>Team Members</Text>
        <Text style={styles.subtitle}>Team: {teamName}</Text>

        {renderTable("Batters", batters, ["#", "Name", "Role", "Captain", "Vice Captain"])}
        {renderTable("Bowlers", bowlers, ["#", "Name", "Role", "Captain", "Vice Captain"])}
        {renderTable("All Rounders", allRounders, ["#", "Name", "Role", "Captain", "Vice Captain"])}
        {renderTable("Wicket Keepers", wicketKeepers, ["#", "Name", "Role", "Captain", "Vice Captain"])}

        <Text style={styles.footer}>
          Generated on: {formatDate(new Date().toISOString())}
        </Text>
      </Page>
    </Document>
  );
};

// Stats PDF Component
const StatsPDF: React.FC<{
  stats: any[];
  teamName: string;
  selectedStat?: string;
}> = ({ stats, teamName, selectedStat = "Most Runs" }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>Team Statistics</Text>
        <Text style={styles.subtitle}>Team: {teamName}</Text>
        <Text style={styles.subtitle}>Stat Type: {selectedStat}</Text>

        {stats && stats.length > 0 ? (
          <View style={styles.section}>
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={[styles.tableCell, { width: "10%" }]}>#</Text>
                <Text style={[styles.tableCell, { width: "30%" }]}>Player</Text>
                <Text style={[styles.tableCell, { width: "15%" }]}>Matches</Text>
                <Text style={[styles.tableCell, { width: "15%" }]}>Inn</Text>
                <Text style={[styles.tableCell, { width: "15%" }]}>Avg</Text>
                <Text style={[styles.tableCellLast, { width: "15%" }]}>Runs</Text>
              </View>
              {stats.map((stat, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={[styles.tableCell, { width: "10%" }]}>
                    {index + 1}
                  </Text>
                  <Text style={[styles.tableCell, { width: "30%" }]}>
                    {stat.playerName || stat.name || "N/A"}
                  </Text>
                  <Text style={[styles.tableCell, { width: "15%" }]}>
                    {stat.matches || stat.Matches || 0}
                  </Text>
                  <Text style={[styles.tableCell, { width: "15%" }]}>
                    {stat.innings || stat.Inn || 0}
                  </Text>
                  <Text style={[styles.tableCell, { width: "15%" }]}>
                    {stat.average || stat.Avg || "0.00"}
                  </Text>
                  <Text style={[styles.tableCellLast, { width: "15%" }]}>
                    {stat.runs || 0}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        ) : (
          <Text style={styles.subtitle}>No statistics available</Text>
        )}

        <Text style={styles.footer}>
          Generated on: {formatDate(new Date().toISOString())}
        </Text>
      </Page>
    </Document>
  );
};

// Matches PDF Component
const MatchesPDF: React.FC<{
  matches: IMatchSummary[];
  teamName: string;
  matchType: "live" | "upcoming" | "past";
}> = ({ matches, teamName, matchType }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>Team Matches</Text>
        <Text style={styles.subtitle}>Team: {teamName}</Text>
        <Text style={styles.subtitle}>
          Match Type: {matchType.charAt(0).toUpperCase() + matchType.slice(1)}
        </Text>

        {matches && matches.length > 0 ? (
          matches.map((match, index) => (
            <View key={index} style={styles.matchCard}>
              <Text style={styles.matchTitle}>Match {index + 1}</Text>
              <View style={styles.matchRow}>
                <Text style={styles.matchLabel}>Date:</Text>
                <Text style={styles.matchValue}>{formatDate(match.startDate)}</Text>
              </View>
              <View style={styles.matchRow}>
                <Text style={styles.matchLabel}>Tournament:</Text>
                <Text style={styles.matchValue}>
                  {match.tournament?.tournamentName || "N/A"}
                </Text>
              </View>
              <View style={styles.matchRow}>
                <Text style={styles.matchLabel}>Team 1:</Text>
                <Text style={styles.matchValue}>
                  {match.teamOne?.teamName || "N/A"}
                </Text>
              </View>
              <View style={styles.matchRow}>
                <Text style={styles.matchLabel}>Team 2:</Text>
                <Text style={styles.matchValue}>
                  {match.teamTwo?.teamName || "N/A"}
                </Text>
              </View>
              <View style={styles.matchRow}>
                <Text style={styles.matchLabel}>Venue:</Text>
                <Text style={styles.matchValue}>
                  {match.groundId?.groundName || "N/A"}
                </Text>
              </View>
              {match.inningScorecardList &&
                match.inningScorecardList.length > 0 && (
                  <View style={styles.matchRow}>
                    <Text style={styles.matchLabel}>Score:</Text>
                    <Text style={styles.matchValue}>
                      {match.inningScorecardList
                        .map(
                          (inning) =>
                            `${inning.runs}/${inning.wicket} (${inning.over}.${inning.ball} overs)`
                        )
                        .join(", ")}
                    </Text>
                  </View>
                )}
              {match.matchResult && (
                <View style={styles.matchRow}>
                  <Text style={styles.matchLabel}>Result:</Text>
                  <Text style={styles.matchValue}>{match.matchResult}</Text>
                </View>
              )}
            </View>
          ))
        ) : (
          <Text style={styles.subtitle}>No matches available</Text>
        )}

        <Text style={styles.footer}>
          Generated on: {formatDate(new Date().toISOString())}
        </Text>
      </Page>
    </Document>
  );
};

// All Tabs PDF Component
const AllTabsPDF: React.FC<{
  members: IPlayers[] | ITeamsSquad[];
  stats: any[];
  matches: IMatchSummary[];
  teamName: string;
  selectedStat?: string;
}> = ({ members, stats, matches, teamName, selectedStat = "Most Runs" }) => {
  return (
    <Document>
      {/* Cover Page */}
      <Page size="A4" style={[styles.page, { justifyContent: "center" }]}>
        <Text style={styles.title}>Team Complete Report</Text>
        <Text style={styles.subtitle}>{teamName}</Text>
        <Text style={styles.footer}>
          Generated on: {formatDate(new Date().toISOString())}
        </Text>
      </Page>

      {/* Members Page */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>1. Team Members</Text>
        <Text style={styles.subtitle}>Team: {teamName}</Text>
        {members && members.length > 0 ? (
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.tableCell, { width: "10%" }]}>#</Text>
              <Text style={[styles.tableCell, { width: "60%" }]}>Name</Text>
              <Text style={[styles.tableCellLast, { width: "30%" }]}>Role</Text>
            </View>
            {members.map((member, index) => (
              <View key={index} style={styles.tableRow}>
                <Text style={[styles.tableCell, { width: "10%" }]}>
                  {index + 1}
                </Text>
                <Text style={[styles.tableCell, { width: "60%" }]}>
                  {getPlayerName(member)}
                </Text>
                <Text style={[styles.tableCellLast, { width: "30%" }]}>
                  {getPlayerRoleDisplay(member)}
                </Text>
              </View>
            ))}
          </View>
        ) : (
          <Text style={styles.subtitle}>No members available</Text>
        )}
      </Page>

      {/* Stats Page */}
      {stats && stats.length > 0 && (
        <Page size="A4" style={styles.page}>
          <Text style={styles.title}>2. Team Statistics</Text>
          <Text style={styles.subtitle}>Team: {teamName}</Text>
          <Text style={styles.subtitle}>Stat Type: {selectedStat}</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.tableCell, { width: "10%" }]}>#</Text>
              <Text style={[styles.tableCell, { width: "40%" }]}>Player</Text>
              <Text style={[styles.tableCell, { width: "25%" }]}>Matches</Text>
              <Text style={[styles.tableCellLast, { width: "25%" }]}>Runs</Text>
            </View>
            {stats.map((stat, index) => (
              <View key={index} style={styles.tableRow}>
                <Text style={[styles.tableCell, { width: "10%" }]}>
                  {index + 1}
                </Text>
                <Text style={[styles.tableCell, { width: "40%" }]}>
                  {stat.playerName || stat.name || "N/A"}
                </Text>
                <Text style={[styles.tableCell, { width: "25%" }]}>
                  {stat.matches || stat.Matches || 0}
                </Text>
                <Text style={[styles.tableCellLast, { width: "25%" }]}>
                  {stat.runs || 0}
                </Text>
              </View>
            ))}
          </View>
        </Page>
      )}

      {/* Matches Page */}
      {matches && matches.length > 0 && (
        <Page size="A4" style={styles.page}>
          <Text style={styles.title}>3. Team Matches</Text>
          <Text style={styles.subtitle}>Team: {teamName}</Text>
          {matches.map((match, index) => (
            <View key={index} style={styles.matchCard}>
              <Text style={styles.matchTitle}>Match {index + 1}</Text>
              <View style={styles.matchRow}>
                <Text style={styles.matchLabel}>Date:</Text>
                <Text style={styles.matchValue}>{formatDate(match.startDate)}</Text>
              </View>
              <View style={styles.matchRow}>
                <Text style={styles.matchLabel}>Tournament:</Text>
                <Text style={styles.matchValue}>
                  {match.tournament?.tournamentName || "N/A"}
                </Text>
              </View>
              <View style={styles.matchRow}>
                <Text style={styles.matchLabel}>Team 1:</Text>
                <Text style={styles.matchValue}>
                  {match.teamOne?.teamName || "N/A"}
                </Text>
              </View>
              <View style={styles.matchRow}>
                <Text style={styles.matchLabel}>Team 2:</Text>
                <Text style={styles.matchValue}>
                  {match.teamTwo?.teamName || "N/A"}
                </Text>
              </View>
              <View style={styles.matchRow}>
                <Text style={styles.matchLabel}>Venue:</Text>
                <Text style={styles.matchValue}>
                  {match.groundId?.groundName || "N/A"}
                </Text>
              </View>
            </View>
          ))}
        </Page>
      )}
    </Document>
  );
};

// Export functions
export const exportMembersToPDF = async (
  members: IPlayers[] | ITeamsSquad[],
  teamName: string
): Promise<void> => {
  const doc = <MembersPDF members={members} teamName={teamName} />;
  const asPdf = pdf(doc);
  const blob = await asPdf.toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${teamName}_Members_${new Date().toISOString().split("T")[0]}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
};

export const exportStatsToPDF = async (
  stats: any[],
  teamName: string,
  selectedStat: string = "Most Runs"
): Promise<void> => {
  const doc = <StatsPDF stats={stats} teamName={teamName} selectedStat={selectedStat} />;
  const asPdf = pdf(doc);
  const blob = await asPdf.toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${teamName}_Stats_${new Date().toISOString().split("T")[0]}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
};

export const exportMatchesToPDF = async (
  matches: IMatchSummary[],
  teamName: string,
  matchType: "live" | "upcoming" | "past" = "past"
): Promise<void> => {
  const doc = <MatchesPDF matches={matches} teamName={teamName} matchType={matchType} />;
  const asPdf = pdf(doc);
  const blob = await asPdf.toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${teamName}_Matches_${matchType}_${new Date().toISOString().split("T")[0]}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
};

export const exportAllTabsToPDF = async (
  members: IPlayers[] | ITeamsSquad[],
  stats: any[],
  matches: IMatchSummary[],
  teamName: string,
  selectedStat: string = "Most Runs"
): Promise<void> => {
  const doc = (
    <AllTabsPDF
      members={members}
      stats={stats}
      matches={matches}
      teamName={teamName}
      selectedStat={selectedStat}
    />
  );
  const asPdf = pdf(doc);
  const blob = await asPdf.toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${teamName}_Complete_Report_${new Date().toISOString().split("T")[0]}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
};


